import React, { Fragment } from 'react';
import PropTypes from 'prop-types';

import { FieldArray } from 'formik';
import { withFormikField } from 'utils/formik';

import { Checkbox, Flexer, Icon, Spacer } from '@ontruck/orc';

import { baseLoad, LOADS_FIELD } from 'modules/shipper-order/Form/constants';

import { AddLoadSection, ExtraInfoSection, LoadSection } from '../';

import { StyledLoadsBox, StyledShipmentBox, StyledShipmentTitle } from './styled';

import { AddressSelector, Calendar, ExtraCheckbox, Hour } from 'modules/shipper-order/Form/fields';

import RemoveStopButton from './RemoveStopButton';
import ReorderStopButtons from './ReorderStopButtons';
import StyledAppointmentContainer from './styled/StyledAppointmentContainer';
import { injectIntl } from 'react-intl';

const handlingIcon = {
  HANDLING_LOADING: 'extra-drop',
  HANDLING_UNLOADING: 'extra-pick',
};

const StopSection = ({
  stopPreffix,
  addressSuffix,
  allowRemove,
  onRemove,
  onNewAddress,
  shipperAddresses,
  userInfo,
  isMainStop,
  showExtraInfo,
  onHandlingExtraChange,
  handlingExtraName,
  handlingExtraChecked,
  isEdition,
  shipment,

  allowMoveUp,
  allowMoveDown,
  onMoveUp,
  onMoveDown,

  field,
  form,
  disabledFields,
  onTimeChange,
  isRepeated,
  intl,
}) => {
  const { values, setFieldValue } = form;
  const { name, value } = field;
  const {
    has_dropoff_appointment: hasDropoffAppointment,
    dropoff_appointment_required: dropoffAppointmentRequired,
  } = shipment;

  if (!value) {
    return null;
  }
  const t = intl.messages.modules.shipper_order.form.stop_section;

  const { shipments = [] } = values;
  const { loads = [] } = value;
  const newLoad = baseLoad({
    item_height: loads[0]?.item_height,
  });
  const isDisabledByDropoffAppointment = dropoffAppointmentRequired && !hasDropoffAppointment;
  const handleHasAppointmentChange = (checked) => {
    const hasAppointmentName = [...name.split('.'), 'has_dropoff_appointment'];
    setFieldValue(hasAppointmentName, checked);
  };

  const handlingType = t.handling_placeholder[addressSuffix];

  // This renders the dates and times inputs for the shipment or appointment
  const renderTimings = (isForAppointment) => {
    const nameFrom = isForAppointment
      ? [name, 'dropoff_appointment', 'time_from']
      : [name, `${stopPreffix}_time_from`];
    const nameTo = isForAppointment
      ? [name, 'dropoff_appointment', 'time_to']
      : [name, `${stopPreffix}_time_to`];

    const firstPickup = isForAppointment ? null : shipments[0]?.pickup_time_from;
    return (
      <Flexer.Parent
        flexDirection="row"
        mRight="S"
        data-testid={
          isForAppointment ? 'stop-section__appointment-datetimes' : 'stop-section__datetimes'
        }
      >
        <Calendar
          labelDay={t.placeholder_day_from}
          firstPickup={firstPickup}
          name={nameFrom}
          onTimeChange={onTimeChange}
          isRepeated={isRepeated}
          disabled={isDisabledByDropoffAppointment}
        />
        <Calendar
          labelDay={t.placeholder_day_to}
          firstPickup={firstPickup}
          name={nameTo}
          onTimeChange={onTimeChange}
          isRepeated={isRepeated}
          disabled={isDisabledByDropoffAppointment}
        />
        <Spacer mRight="XS" />
        <Hour
          labelHour={t.placeholder_time_from}
          name={nameFrom}
          onTimeChange={onTimeChange}
          disabled={isDisabledByDropoffAppointment}
        />
        <Hour
          labelHour={t.placeholder_time_to}
          name={nameTo}
          onTimeChange={onTimeChange}
          disabled={isDisabledByDropoffAppointment}
        />
      </Flexer.Parent>
    );
  };

  return (
    <StyledShipmentBox mBottom="M" data-testid="stop-section">
      <StyledShipmentTitle>{t.titles[addressSuffix]}</StyledShipmentTitle>
      <Flexer.Parent alignItems="center">
        <Flexer.Child
          flexGrow="0"
          mRight="S"
          data-e2e={`shipperOrder_edit__address_${addressSuffix}`}
        >
          <AddressSelector
            name={[name, `address_${addressSuffix}`]}
            placeholder={t.address_placeholder[addressSuffix]}
            addresses={shipperAddresses}
            onSaveAddress={onNewAddress}
            zipcodeRE={userInfo.zipcodeRE}
            country={userInfo.country}
            onChange={(address) => {
              isMainStop &&
                setFieldValue(
                  'shipments',
                  shipments.map((s) => ({
                    ...s,
                    [`address_${addressSuffix}`]: address,
                  }))
                );
            }}
            disabled={disabledFields.includes('address')}
          />
        </Flexer.Child>
        <Flexer.Child>
          {isEdition && dropoffAppointmentRequired && stopPreffix === 'dropoff' && (
            <StyledAppointmentContainer>
              <Checkbox
                checked={hasDropoffAppointment}
                onChange={(event, { value: checked }) => handleHasAppointmentChange(checked)}
                label={t.set_up_appointment_hours}
              />
            </StyledAppointmentContainer>
          )}
          <div style={{ display: hasDropoffAppointment ? 'inherit' : 'none' }}>
            {renderTimings(true)}
          </div>
          <div style={{ display: hasDropoffAppointment ? 'none' : 'inherit' }}>
            {renderTimings(false)}
          </div>
        </Flexer.Child>
        <Flexer.Child>
          <ExtraCheckbox
            label={
              <Fragment>
                {handlingType}&nbsp;&nbsp;
                <Icon glyph={handlingIcon[handlingExtraName]} size="2.1rem" color="greyWolf" />
              </Fragment>
            }
            checked={handlingExtraChecked}
            onChange={(event, { value }) => onHandlingExtraChange(handlingExtraName, value)}
          />
        </Flexer.Child>
      </Flexer.Parent>
      {!isMainStop && (
        <Spacer mTop="M">
          <FieldArray
            name={`${name}.loads`}
            render={(loadsList) => (
              <StyledLoadsBox>
                {loads.map((load, key) => (
                  <LoadSection
                    name={[name, LOADS_FIELD, key]}
                    key={key}
                    onDelete={() => loadsList.remove(key)}
                    showClear={loads.length > 1}
                  />
                ))}
                <AddLoadSection onClick={() => loadsList.push(newLoad)} />
              </StyledLoadsBox>
            )}
          />
        </Spacer>
      )}
      {showExtraInfo && (
        <Flexer.Parent mTop="M">
          <ExtraInfoSection name={name} stopPreffix={stopPreffix} disabledFields={disabledFields} />
        </Flexer.Parent>
      )}
      {allowRemove && (
        <Fragment>
          <RemoveStopButton onClick={onRemove} styledPosition={[0, 0]} />
          <ReorderStopButtons
            styledPosition={['50%', 0]}
            allowMoveUp={allowMoveUp}
            allowMoveDown={allowMoveDown}
            onUpClick={onMoveUp}
            onDownClick={onMoveDown}
          />
        </Fragment>
      )}
    </StyledShipmentBox>
  );
};

StopSection.propTypes = {
  stopPreffix: PropTypes.oneOf(['pickup', 'dropoff']),
  addressSuffix: PropTypes.oneOf(['from', 'to']),
  allowRemove: PropTypes.bool,
  onNewAddress: PropTypes.func,
  isMainStop: PropTypes.bool,
  showExtraInfo: PropTypes.bool,
  onRemove: PropTypes.func,
  shipperAddresses: PropTypes.array,
  userInfo: PropTypes.object.isRequired,
  onHandlingExtraChange: PropTypes.func,
  handlingExtraName: PropTypes.string.isRequired,
  handlingExtraChecked: PropTypes.bool,
  allowMoveUp: PropTypes.bool,
  allowMoveDown: PropTypes.bool,
  onMoveUp: PropTypes.func.isRequired,
  onMoveDown: PropTypes.func.isRequired,
  field: PropTypes.object,
  form: PropTypes.object,
  disabledFields: PropTypes.array.isRequired,
  onTimeChange: PropTypes.func,
  isRepeated: PropTypes.bool,
  isEdition: PropTypes.bool,
  shipment: PropTypes.object,
};

StopSection.defaultProps = {
  shipment: {},
  appointmentRequired: false,
  addressSuffix: 'from',
  allowRemove: false,
  onNewAddress: () => {
    /*noop*/
  },
  isMainStop: false,
  showExtraInfo: true,
  onRemove: () => {
    /*noop*/
  },
  shipperAddresses: [],
  onHandlingExtraChange: () => {
    /*noop*/
  },
  handlingExtraChecked: false,
  field: {},
  form: {},
  disabledFields: [],
  onTimeChange: undefined,
};

export default withFormikField(injectIntl(StopSection));
